import { SUPPORTED_LANGUAGES, formatLanguage } from 'constants/languages';

export interface PrintingsFilterState {
  foil: string[];
  /** Frontend uppercase codes ('EN', 'ZHS') — never display names or backend codes. */
  languages: string[];
  conditions: string[];
}

export const FOIL_OPTIONS = ['Foil', 'Regular'];
export const CONDITION_OPTIONS = ['NM', 'LP', 'MP', 'HP'];
/**
 * Chips display the full name but toggle the CODE. The state's language values
 * arrive from the page as codes (saved want-list defaults are 'EN', edit-mode
 * entries are backend 'en'), so a chip comparing display names never matched —
 * clicking English appended 'English' to twelve codes, the dimension diverged
 * from its load default, and the token row lit up with every language at once.
 */
export const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map((l) => ({
  value: l.code,
  label: l.displayName,
}));

/** How many language chips show before the "+N more" expander. */
export const LANGUAGE_COLLAPSE_AT = 6;

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v) => b.includes(v));

/**
 * The badge counts APPLIED values -- those differing from what the page loaded
 * with. In edit mode the load-time values are the user's saved want-list
 * preferences, so "no badge" correctly means "unchanged from what you saved",
 * not "no filters at all".
 */
export const countAppliedFilters = (
  current: PrintingsFilterState,
  defaults: PrintingsFilterState,
): number => {
  let n = 0;
  if (!sameSet(current.foil, defaults.foil)) n += 1;
  if (!sameSet(current.languages, defaults.languages)) n += 1;
  if (!sameSet(current.conditions, defaults.conditions)) n += 1;
  return n;
};

export interface PrintingsToken {
  id: string;
  label: string;
  dimension: keyof PrintingsFilterState;
  value: string;
}

/**
 * One token per applied VALUE, so the x removes exactly that value.
 *
 * A dimension only produces tokens once it differs from the load-time default;
 * otherwise every page would open showing a dozen "English", "NM" tokens that
 * the user never chose.
 */
export const buildTokens = (
  current: PrintingsFilterState,
  defaults: PrintingsFilterState,
): PrintingsToken[] => {
  const tokens: PrintingsToken[] = [];
  const push = (dimension: keyof PrintingsFilterState, values: string[]) => {
    values.forEach((value) =>
      tokens.push({
        id: `${dimension}:${value}`,
        // Language values are codes; show the badge abbreviation ('EN', 'JP').
        label: dimension === 'languages' ? formatLanguage(value) : value,
        dimension,
        value,
      }),
    );
  };

  if (!sameSet(current.foil, defaults.foil)) push('foil', current.foil);
  if (!sameSet(current.languages, defaults.languages)) push('languages', current.languages);
  if (!sameSet(current.conditions, defaults.conditions)) push('conditions', current.conditions);

  return tokens;
};
